/**
 * Helpers shared between the on-screen Unified Overview and the
 * Excel/PDF exports.
 *
 * Availability now comes from *project team-member assignments*, not
 * from Manpower Plans (plans are still authored on their own tab but
 * they're just draft proposals; the Unified view has to reflect the
 * source of truth: who's on which project team on a given date).
 */

import { parseISO, isValid, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';

const _toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isValid(v) ? startOfDay(v) : null;
  // Accept both `YYYY-MM-DD` and ISO strings with time / tz.
  const s = String(v);
  const d = s.length >= 10 ? parseISO(s.slice(0, 10)) : parseISO(s);
  return isValid(d) ? startOfDay(d) : null;
};

const _within = (date, from, to) => {
  if (!date) return false;
  const d = startOfDay(date);
  if (from && (isBefore(d, from) && !isEqual(d, from))) return false;
  if (to && (isAfter(d, to) && !isEqual(d, to))) return false;
  return true;
};

/**
 * Is the given TeamMember on-site on `date`?
 *
 * Rules:
 *   - `whole_duration !== false` → assigned for the project's whole
 *     start_date → planned_end_date (or actual_end_date) range.
 *   - Otherwise → true iff any of the `spans` covers `date`.
 *   - A missing project end-date is treated as "open ended" — the
 *     employee stays assigned until the project is closed.
 *   - A missing project start-date is treated as "started already".
 */
export function isMemberAssignedOnDate(project, member, date) {
  const d = _toDate(date);
  if (!d) return false;
  const projStart = _toDate(project?.start_date);
  const projEnd = _toDate(project?.actual_end_date || project?.planned_end_date);
  if (member?.whole_duration !== false) {
    return _within(d, projStart, projEnd);
  }
  const spans = member?.spans || [];
  for (const sp of spans) {
    const from = _toDate(sp?.from_date);
    const to = _toDate(sp?.to_date);
    if (from || to) {
      if (_within(d, from, to)) return true;
    }
  }
  return false;
}

/**
 * For a given date, return a list of `{ employee_id, employee_name,
 * project_id, project_name, role }` for every assignment active that
 * day across the supplied projects.
 */
export function getAssignmentsForDate(projects, employees, date) {
  const empById = new Map((employees || []).map((e) => [e.id, e]));
  const out = [];
  for (const p of projects || []) {
    // Ignore inactive/completed projects when their end-date has passed
    // — actual_end_date already gates them via isMemberAssignedOnDate.
    if (p.is_active === false) continue;
    for (const m of p.team_members || []) {
      if (!m?.employee_id) continue;
      if (!isMemberAssignedOnDate(p, m, date)) continue;
      const emp = empById.get(m.employee_id);
      out.push({
        employee_id: m.employee_id,
        employee_name: emp?.name || 'Unknown',
        project_id: p.id,
        project_name: p.name,
        role: m.role || '',
      });
    }
  }
  return out;
}

/**
 * Map employee id → sorted list of active projects on `date`.
 */
export function getEmployeeProjectsOnDate(projects, empId, date) {
  const out = [];
  for (const p of projects || []) {
    if (p.is_active === false) continue;
    const m = (p.team_members || []).find((tm) => tm.employee_id === empId);
    if (!m) continue;
    if (isMemberAssignedOnDate(p, m, date)) out.push(p);
  }
  return out;
}

// ─── Conflict detection ─────────────────────────────────────────────

/** Convert `whole_duration` + `spans` on a member to a list of
 *  concrete `{start:Date, end:Date}` intervals. Members with
 *  `whole_duration` inherit the project's start / end (open-ended if
 *  end is missing — treated as "far future" for overlap purposes).
 */
function _memberIntervals(project, member) {
  if (member?.whole_duration !== false) {
    const s = _toDate(project?.start_date);
    const e = _toDate(project?.actual_end_date || project?.planned_end_date);
    if (!s && !e) return [];  // no dates at all — can't meaningfully overlap
    return [{ start: s, end: e }];
  }
  const out = [];
  for (const sp of member?.spans || []) {
    if (!sp) continue;
    const s = _toDate(sp.from_date);
    const e = _toDate(sp.to_date);
    if (s || e) out.push({ start: s, end: e });
  }
  return out;
}

/** Intersect two intervals. Missing bounds are treated as
 *  ±infinity, which is what a user means by "no end date yet". */
function _intersect(a, b) {
  const START = a.start && b.start
    ? (isAfter(a.start, b.start) ? a.start : b.start)
    : (a.start || b.start || null);
  const END = a.end && b.end
    ? (isBefore(a.end, b.end) ? a.end : b.end)
    : (a.end || b.end || null);
  if (START && END && isAfter(START, END)) return null;
  return { start: START, end: END };
}

/**
 * Find every OTHER project + member pair whose assignment window
 * overlaps the given `(currentProject, member)` — i.e. this employee
 * would be double-booked. Returns:
 *
 *   [{ project_id, project_name, role, intervals:[{start,end}] }]
 *
 * Excludes `currentProject.id` itself.
 */
export function findAssignmentConflicts(currentProject, member, otherProjects) {
  if (!member?.employee_id) return [];
  const myIntervals = _memberIntervals(currentProject, member);
  if (myIntervals.length === 0) return [];
  const out = [];
  for (const p of otherProjects || []) {
    if (!p || p.id === currentProject?.id) continue;
    if (p.is_active === false) continue;
    const otherMembers = (p.team_members || []).filter((m) => m.employee_id === member.employee_id);
    for (const om of otherMembers) {
      const theirIntervals = _memberIntervals(p, om);
      const overlaps = [];
      for (const a of myIntervals) {
        for (const b of theirIntervals) {
          const x = _intersect(a, b);
          if (x) overlaps.push(x);
        }
      }
      if (overlaps.length > 0) {
        out.push({
          project_id: p.id,
          project_name: p.name || '(untitled)',
          role: om.role || '',
          intervals: overlaps,
        });
      }
    }
  }
  return out;
}
